import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DragDropContext } from 'react-beautiful-dnd';
import { updateJob } from '../../redux/features/jobSlice';
import JobQueue from './JobQueue';
import CalendarWithDragDrop from './CalendarWithDragDrop';
import JobForm from '../forms/JobForm';
import { Plus, Calendar, List, Send, LayoutGrid } from 'lucide-react';
import { toast } from 'react-toastify';
import { canUserCreateJobs, getSchedulerAudience, getWorkerPermissionsFromStorage } from '../../utils/profileRoleUtils';

const viewButtonClass = (active, activeTone) =>
  `px-1 py-1 sm:px-3 sm:py-1.5 text-[10px] leading-tight sm:text-sm rounded-md flex items-center justify-center gap-1 sm:gap-1.5 transition-colors flex-1 sm:flex-initial sm:w-auto ${
    active
      ? `${activeTone} text-white shadow-sm`
      : 'text-gray-400 hover:text-white hover:bg-white/5'
  }`;

const JobSchedulingDashboard = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { jobs, jobsFetching } = useSelector((state) => state.job);
  const { userType } = useSelector((state) => state.auth);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobForm, setShowJobForm] = useState(false);
  const [viewMode, setViewMode] = useState('split');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const hasSeenLoadingRef = useRef(false);

  const effectiveUserType = useMemo(() => {
    if (typeof window !== 'undefined') {
      const storedUserType = localStorage.getItem('userType');
      if (storedUserType) {
        return storedUserType;
      }
    }
    return userType || 'company';
  }, [userType]);

  const workerPermissions = useMemo(() => getWorkerPermissionsFromStorage(), []);
  const calendarSchedulerAudience = getSchedulerAudience(effectiveUserType, workerPermissions);
  const canCreateJobs = canUserCreateJobs(effectiveUserType, workerPermissions);

  const handleJobSelect = (job) => {
    setSelectedJob(job);
  };

  const handleJobSchedule = async (jobId, scheduledDate, scheduledTime) => {
    try {
      const job = jobs.find(j => j._id === jobId);
      if (!job) return;

      const updatedJob = {
        serviceDate: scheduledDate.toISOString(),
        scheduledTime: scheduledTime,
        status: 'scheduled'
      };

      await dispatch(updateJob({ id: jobId, jobData: updatedJob }));
      toast.success(t('jobs.statusUpdated'));
    } catch (error) {
      console.error('Error scheduling job:', error);
      toast.error(t('jobs.errorUpdatingStatus'));
    }
  };

  const handleDragEnd = (result) => {
    const { destination, draggableId } = result;

    if (!destination) return;

    if (destination.droppableId.startsWith('time-slot-')) {
      const [, , dateStr, hour, minute] = destination.droppableId.split('-');
      const scheduledDate = new Date(dateStr);
      const scheduledTime = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;

      handleJobSchedule(draggableId, scheduledDate, scheduledTime);
    }
  };

  const handleJobMove = async (jobId, newDate, newTime) => {
    try {
      const job = jobs.find(j => j._id === jobId);
      if (!job) return;

      await dispatch(updateJob({
        id: jobId,
        jobData: {
          serviceDate: newDate.toISOString(),
          scheduledTime: newTime
        }
      }));
    } catch (error) {
      console.error('Error moving job:', error);
    }
  };

  const handleJobFormClose = () => {
    setShowJobForm(false);
    setSelectedJob(null);
  };

  const handleJobFormSubmit = () => {
    setShowJobForm(false);
    setSelectedJob(null);
  };

  useEffect(() => {
    if (jobsFetching) {
      hasSeenLoadingRef.current = true;
      return;
    }

    if (hasSeenLoadingRef.current && !hasLoadedOnce) {
      setHasLoadedOnce(true);
    }
  }, [jobsFetching, hasLoadedOnce]);

  useEffect(() => {
    if (!hasLoadedOnce && Array.isArray(jobs) && jobs.length > 0) {
      setHasLoadedOnce(true);
    }
  }, [jobs, hasLoadedOnce]);

  const showInitialLoader = jobsFetching && !hasLoadedOnce;

  if (showInitialLoader) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  const showCalendar = viewMode === 'split' || viewMode === 'calendar';
  const showUnscheduled = viewMode === 'split' || viewMode === 'queue';
  const showForwarded = viewMode === 'split' || viewMode === 'forwarded';

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-slate-950 via-gray-900 to-gray-900 p-2 sm:p-4">
      {jobsFetching && hasLoadedOnce && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sky-500"></div>
        </div>
      )}

      <div className="mb-3 sm:mb-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <div className="flex items-center justify-between gap-2 sm:contents">
            <h1 className="text-base sm:text-2xl font-semibold text-white tracking-tight">{t('jobs.jobs')}</h1>
            {canCreateJobs && (
              <button
                onClick={() => setShowJobForm(true)}
                className="sm:order-3 px-2 py-1 sm:px-3 sm:py-1.5 text-[11px] sm:text-sm bg-green-500 text-white rounded-md hover:bg-green-400 flex items-center gap-1 sm:gap-1.5 flex-shrink-0 shadow-md shadow-green-500/35"
              >
                <Plus className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="whitespace-nowrap">{t('jobs.newJob')}</span>
              </button>
            )}
          </div>

          <div className="flex gap-0.5 p-0.5 bg-gray-800/80 border border-white/10 rounded-lg sm:gap-1 sm:p-1 sm:flex-wrap sm:w-fit sm:order-2">
            <button
              onClick={() => setViewMode('split')}
              className={viewButtonClass(viewMode === 'split', 'bg-slate-600')}
            >
              <LayoutGrid className="hidden sm:block h-3.5 w-3.5 flex-shrink-0" />
              <span>{t('jobs.combined')}</span>
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={viewButtonClass(viewMode === 'calendar', 'bg-sky-600')}
            >
              <Calendar className="hidden sm:block h-3.5 w-3.5 flex-shrink-0" />
              <span>{t('jobs.scheduled')}</span>
            </button>
            <button
              onClick={() => setViewMode('queue')}
              className={viewButtonClass(viewMode === 'queue', 'bg-amber-600')}
            >
              <List className="hidden sm:block h-3.5 w-3.5 flex-shrink-0" />
              <span>{t('jobs.queueOnly')}</span>
            </button>
            <button
              onClick={() => setViewMode('forwarded')}
              className={viewButtonClass(viewMode === 'forwarded', 'bg-rose-600')}
            >
              <Send className="hidden sm:block h-3.5 w-3.5 flex-shrink-0" />
              <span className="sm:hidden">Prosleđeni</span>
              <span className="hidden sm:inline">{t('jobs.forwardedJobs')}</span>
            </button>
          </div>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid gap-3 sm:gap-5 mb-20 sm:mb-4">
          {showCalendar && (
            <div className="w-full min-w-0 overflow-hidden">
              <CalendarWithDragDrop
                jobs={jobs}
                onJobSchedule={handleJobSchedule}
                onJobMove={handleJobMove}
                isNested={true}
                schedulerAudience={calendarSchedulerAudience}
              />
            </div>
          )}

          {showUnscheduled && (
            <div className="w-full flex-shrink-0">
              <JobQueue
                jobs={jobs}
                loading={false}
                onJobSelect={handleJobSelect}
                selectedJobId={selectedJob?._id}
                onJobSchedule={handleJobSchedule}
                variant="unscheduled"
              />
            </div>
          )}

          {showForwarded && (
            <div className="w-full flex-shrink-0">
              <JobQueue
                jobs={jobs}
                loading={false}
                onJobSelect={handleJobSelect}
                selectedJobId={selectedJob?._id}
                onJobSchedule={handleJobSchedule}
                variant="forwarded"
              />
            </div>
          )}
        </div>
      </DragDropContext>

      {showJobForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <JobForm
              onClose={handleJobFormClose}
              onSubmit={handleJobFormSubmit}
              initialJobData={selectedJob}
            />
          </div>
        </div>
      )}

      {selectedJob && viewMode !== 'split' && (
        <div className="fixed right-4 top-20 w-80 bg-gray-800 rounded-xl p-4 shadow-lg border border-white/10">
          <h3 className="text-lg font-medium text-white mb-2">{t('jobs.jobDetails')}</h3>
          <div className="space-y-2 text-sm text-gray-300">
            <div><strong>{t('jobs.client')}:</strong> {selectedJob.clientName}</div>
            <div><strong>{t('jobs.phone')}:</strong> {selectedJob.clientPhone}</div>
            <div><strong>{t('jobs.issueDescription')}:</strong> {selectedJob.issueDescription}</div>
            <div><strong>{t('jobs.status')}:</strong> {selectedJob.status}</div>
            <div><strong>{t('jobs.priority')}:</strong> {selectedJob.priority}</div>
            {selectedJob.serviceDate && (
              <div><strong>{t('jobs.scheduled')}:</strong> {new Date(selectedJob.serviceDate).toLocaleDateString()} {selectedJob.scheduledTime}</div>
            )}
          </div>
          <button
            onClick={() => setSelectedJob(null)}
            className="mt-4 px-3 py-1 bg-gray-700 text-white rounded-md hover:bg-gray-600"
          >
            {t('common.close')}
          </button>
        </div>
      )}
    </div>
  );
};

export default JobSchedulingDashboard;
